import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { ConfirmDialog } from '@/components/ui';
import type { CleaningSector, CleaningSubSector, CleaningEquipmentItem, CleaningTask } from '@haccp/api-client';

const SECTOR_ICONS = [
  '🍳', '👨‍🍳', '🚽', '📦', '🚿', '📮',
  '🧹', '🧴', '🧺', '🗑️', '🪣', '🧼',
  '🍽️', '🥘', '🫙', '❄️', '🔥', '🪴',
  '🚪', '🏪', '🏢', '🛒', '🚚', '📋',
];

const SUBSECTOR_ICONS = [
  '📍', '🔲', '🪑', '🛁', '🚿', '🚰',
  '🍽️', '🥄', '🔪', '🫕', '🥩', '🧊',
  '🗄️', '📦', '🗃️', '🧹', '🧽', '🪣',
  '🚪', '🪟', '💡', '🔌', '🧯', '🪜',
];

function IconPicker({ icons, value, onChange }: { icons: string[]; value: string; onChange: (v: string) => void }) {
  return (
    <div className="grid grid-cols-8 gap-1 p-2 bg-gray-50 border border-gray-200 rounded-lg mt-2">
      {icons.map((icon) => (
        <button
          key={icon}
          type="button"
          onClick={() => onChange(icon)}
          className={`text-xl p-1.5 rounded-lg hover:bg-blue-100 transition ${value === icon ? 'bg-blue-200 ring-2 ring-blue-400' : ''}`}
        >
          {icon}
        </button>
      ))}
    </div>
  );
}

export default function CleaningConfigPage() {
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const [newSectorName, setNewSectorName] = useState('');
  const [newSectorIcon, setNewSectorIcon] = useState('🍳');
  const [showSectorIconPicker, setShowSectorIconPicker] = useState(false);

  const [addingSubSectorForSector, setAddingSubSectorForSector] = useState<string | null>(null);
  const [newSubSectorName, setNewSubSectorName] = useState('');
  const [newSubSectorIcon, setNewSubSectorIcon] = useState('📍');
  const [showSubSectorIconPicker, setShowSubSectorIconPicker] = useState(false);

  const [addingEquipmentForSubSector, setAddingEquipmentForSubSector] = useState<string | null>(null);
  const [newEquipmentName, setNewEquipmentName] = useState('');
  const [addingTaskForEquipment, setAddingTaskForEquipment] = useState<string | null>(null);
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskFrequency, setNewTaskFrequency] = useState<'DAILY' | 'WEEKLY'>('DAILY');
  const [editingTask, setEditingTask] = useState<string | null>(null);
  const [editTaskName, setEditTaskName] = useState('');
  const [expandedSectors, setExpandedSectors] = useState<Set<string>>(new Set());
  const [expandedSubSectors, setExpandedSubSectors] = useState<Set<string>>(new Set());
  const [expandedEquipment, setExpandedEquipment] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'sector' | 'subsector' | 'equipment' | 'task'; id: string; name: string } | null>(null);

  const { data: sectors = [], isLoading } = useQuery({
    queryKey: ['cleaning-sectors'],
    queryFn: () => apiClient.getCleaningSectors(),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['cleaning-sectors'] });

  // ─── Mutations ─────────────────────────────────────
  const createSector = useMutation({
    mutationFn: (data: { name: string; icon?: string }) => apiClient.createCleaningSector(data.name, data.icon),
    onSuccess: () => {
      invalidate();
      setNewSectorName('');
      setNewSectorIcon('🍳');
      setShowSectorIconPicker(false);
      addToast(t('cleaning.sectorAdded'), 'success');
    },
    onError: (e: any) => addToast(e.response?.data?.message || t('common.error'), 'error'),
  });

  const deleteSector = useMutation({
    mutationFn: (id: string) => apiClient.deleteCleaningSector(id),
    onSuccess: () => { invalidate(); addToast(t('cleaning.sectorDeleted'), 'success'); },
    onError: (e: any) => addToast(e.response?.data?.message || t('cleaning.deleteError'), 'error'),
  });

  const createSubSector = useMutation({
    mutationFn: ({ sectorId, name, icon }: { sectorId: string; name: string; icon?: string }) =>
      apiClient.createCleaningSubSector(sectorId, name, icon),
    onSuccess: () => {
      invalidate();
      setNewSubSectorName('');
      setNewSubSectorIcon('📍');
      setShowSubSectorIconPicker(false);
      setAddingSubSectorForSector(null);
      addToast(t('cleaning.subSectorAdded'), 'success');
    },
    onError: (e: any) => addToast(e.response?.data?.message || t('common.error'), 'error'),
  });

  const deleteSubSector = useMutation({
    mutationFn: (id: string) => apiClient.deleteCleaningSubSector(id),
    onSuccess: () => { invalidate(); addToast(t('cleaning.subSectorDeleted'), 'success'); },
    onError: (e: any) => addToast(e.response?.data?.message || t('cleaning.deleteError'), 'error'),
  });

  const createEquipment = useMutation({
    mutationFn: ({ subSectorId, name }: { subSectorId: string; name: string }) =>
      apiClient.createCleaningEquipment(subSectorId, name),
    onSuccess: () => { invalidate(); setNewEquipmentName(''); setAddingEquipmentForSubSector(null); addToast(t('cleaning.equipmentAdded'), 'success'); },
    onError: (e: any) => addToast(e.response?.data?.message || t('common.error'), 'error'),
  });

  const deleteEquipment = useMutation({
    mutationFn: (id: string) => apiClient.deleteCleaningEquipment(id),
    onSuccess: () => { invalidate(); addToast(t('cleaning.equipmentDeleted'), 'success'); },
    onError: (e: any) => addToast(e.response?.data?.message || t('cleaning.deleteError'), 'error'),
  });

  const createTask = useMutation({
    mutationFn: ({ equipmentId, name, frequency }: { equipmentId: string; name: string; frequency: 'DAILY' | 'WEEKLY' }) =>
      apiClient.createCleaningTask(equipmentId, name, frequency),
    onSuccess: () => { invalidate(); setNewTaskName(''); setAddingTaskForEquipment(null); addToast(t('cleaning.taskAdded'), 'success'); },
    onError: (e: any) => addToast(e.response?.data?.message || t('common.error'), 'error'),
  });

  const deleteTask = useMutation({
    mutationFn: (id: string) => apiClient.deleteCleaningTask(id),
    onSuccess: () => { invalidate(); addToast(t('cleaning.taskDeleted'), 'success'); },
    onError: (e: any) => addToast(e.response?.data?.message || t('cleaning.deleteError'), 'error'),
  });

  const updateTaskFreq = useMutation({
    mutationFn: ({ id, frequency }: { id: string; frequency: 'DAILY' | 'WEEKLY' }) =>
      apiClient.updateCleaningTask(id, { frequency }),
    onSuccess: () => { invalidate(); },
    onError: (e: any) => addToast(e.response?.data?.message || t('cleaning.updateError'), 'error'),
  });

  const updateTaskName = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      apiClient.updateCleaningTask(id, { name }),
    onSuccess: () => { invalidate(); setEditingTask(null); addToast(t('cleaning.taskUpdated'), 'success'); },
    onError: (e: any) => addToast(e.response?.data?.message || t('common.error'), 'error'),
  });

  const toggleSector = (id: string) => {
    const s = new Set(expandedSectors);
    s.has(id) ? s.delete(id) : s.add(id);
    setExpandedSectors(s);
  };

  const toggleSubSector = (id: string) => {
    const s = new Set(expandedSubSectors);
    s.has(id) ? s.delete(id) : s.add(id);
    setExpandedSubSectors(s);
  };

  const toggleEquipment = (id: string) => {
    const s = new Set(expandedEquipment);
    s.has(id) ? s.delete(id) : s.add(id);
    setExpandedEquipment(s);
  };

  const handleAddDefaultSectors = async () => {
    const defaultSectors = [
      { name: t('cleaning.defaultSector5'), icon: '👨‍🍳' },
      { name: t('cleaning.defaultSector1'), icon: '🍳' },
      { name: t('cleaning.defaultSector2'), icon: '🚽' },
      { name: t('cleaning.defaultSector4'), icon: '📦' },
      { name: t('cleaning.defaultSector3'), icon: '🚿' },
      { name: t('cleaning.defaultSector6'), icon: '📮' },
    ];
    const existing = new Set(sectors.map((s: CleaningSector) => s.name));
    for (const sector of defaultSectors) {
      if (!existing.has(sector.name)) {
        await apiClient.createCleaningSector(sector.name, sector.icon);
      }
    }
    invalidate();
    addToast(t('cleaning.defaultSectorsAdded'), 'success');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="p-3 bg-gray-100 rounded-lg">
              <span className="text-2xl">⚙️</span>
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('cleaning.config')}</h1>
              <p className="text-gray-600 mt-1">{t('cleaning.configSubtitle')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Add sector */}
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">{t('cleaning.addSector')}</h2>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={newSectorName}
                  onChange={(e) => setNewSectorName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && newSectorName.trim() && createSector.mutate({ name: newSectorName.trim(), icon: newSectorIcon })}
                  placeholder={t('cleaning.sectorNamePlaceholder')}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
                <div className="flex gap-2 items-start">
                  <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => setShowSectorIconPicker((v) => !v)}
                      className="w-16 h-12 text-2xl flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      title={t('cleaning.chooseIcon')}
                    >
                      {newSectorIcon}
                    </button>
                    {showSectorIconPicker && (
                      <div className="absolute z-10 mt-14">
                        <IconPicker
                          icons={SECTOR_ICONS}
                          value={newSectorIcon}
                          onChange={(icon) => { setNewSectorIcon(icon); setShowSectorIconPicker(false); }}
                        />
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => newSectorName.trim() && createSector.mutate({ name: newSectorName.trim(), icon: newSectorIcon })}
                    disabled={!newSectorName.trim() || createSector.isPending}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition"
                  >
                    + {t('common.add')}
                  </button>
                </div>
              </div>
              {showSectorIconPicker && (
                <IconPicker
                  icons={SECTOR_ICONS}
                  value={newSectorIcon}
                  onChange={(icon) => { setNewSectorIcon(icon); setShowSectorIconPicker(false); }}
                />
              )}
              {sectors.length === 0 && (
                <button
                  onClick={handleAddDefaultSectors}
                  className="mt-4 px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                >
                  📋 {t('cleaning.addDefaultSectors')}
                </button>
              )}
            </div>

            {/* Sectors list */}
            {sectors.map((sector: CleaningSector) => (
              <div key={sector.id} className="bg-white rounded-xl shadow overflow-hidden border-l-4 border-blue-500">
                {/* Sector header */}
                <div
                  className="flex items-center justify-between p-5 cursor-pointer hover:bg-gray-50 transition"
                  onClick={() => toggleSector(sector.id)}
                >
                  <div className="flex items-center gap-3">
                    <span className={`transform transition-transform ${expandedSectors.has(sector.id) ? 'rotate-90' : ''}`}>▶</span>
                    <span className="text-2xl">{sector.icon || '🏢'}</span>
                    <h3 className="text-lg font-semibold text-gray-900">{sector.name}</h3>
                    {sector.isSystemZone && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">{t('cleaning.systemZone')}</span>}
                    <span className="text-sm text-gray-500">({sector.subSectors?.length || 0} {t('cleaning.subSectorCount')})</span>
                  </div>
                  {!sector.isSystemZone && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget({ type: 'sector', id: sector.id, name: sector.name }); }}
                      className="text-red-500 hover:text-red-700 text-sm font-medium px-3 py-1 rounded hover:bg-red-50 transition"
                    >
                      🗑️ {t('common.delete')}
                    </button>
                  )}
                </div>

                {/* Expanded content - Sub-sectors */}
                {expandedSectors.has(sector.id) && (
                  <div className="border-t border-gray-200 p-5 space-y-4">
                    {/* Sub-sectors list */}
                    {sector.subSectors?.map((subSector: CleaningSubSector) => (
                      <div key={subSector.id} className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
                        {/* Sub-sector header */}
                        <div
                          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-100 transition"
                          onClick={() => toggleSubSector(subSector.id)}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`text-sm transform transition-transform ${expandedSubSectors.has(subSector.id) ? 'rotate-90' : ''}`}>▶</span>
                            <span className="text-lg">{subSector.icon || '📍'}</span>
                            <span className="font-semibold text-gray-800">{subSector.name}</span>
                            <span className="text-xs text-gray-500">({subSector.equipment?.length || 0} {t('cleaning.equipmentCount')})</span>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget({ type: 'subsector', id: subSector.id, name: subSector.name }); }}
                            className="text-red-400 hover:text-red-600 text-xs px-2 py-1 rounded hover:bg-red-50 transition"
                          >
                            {t('common.delete')}
                          </button>
                        </div>

                        {/* Sub-sector expanded content - Equipment */}
                        {expandedSubSectors.has(subSector.id) && (
                          <div className="border-t border-gray-200 px-4 py-3 space-y-4">
                            {/* Equipment list */}
                            {subSector.equipment?.map((eq: CleaningEquipmentItem) => (
                              <div key={eq.id} className="bg-white rounded-lg overflow-hidden border border-gray-200">
                                <div
                                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition"
                                  onClick={() => toggleEquipment(eq.id)}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className={`text-sm transform transition-transform ${expandedEquipment.has(eq.id) ? 'rotate-90' : ''}`}>▶</span>
                                    <span className="font-semibold text-gray-800">{eq.name}</span>
                                    <span className="text-xs text-gray-500">({eq.tasks?.length || 0} {t('cleaning.taskCount')})</span>
                                  </div>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setDeleteTarget({ type: 'equipment', id: eq.id, name: eq.name }); }}
                                    className="text-red-400 hover:text-red-600 text-xs px-2 py-1 rounded hover:bg-red-50 transition"
                                  >
                                    {t('common.delete')}
                                  </button>
                                </div>

                                {/* Tasks */}
                                {expandedEquipment.has(eq.id) && (
                                  <div className="border-t border-gray-200 px-4 py-3 space-y-2">
                                    {eq.tasks?.map((task: CleaningTask) => (
                                      <div key={task.id} className="flex items-center justify-between bg-blue-50 rounded-lg px-4 py-3 border border-gray-200">
                                        <div className="flex items-center gap-3">
                                          {editingTask === task.id ? (
                                            <input
                                              type="text"
                                              value={editTaskName}
                                              onChange={(e) => setEditTaskName(e.target.value)}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter' && editTaskName.trim()) updateTaskName.mutate({ id: task.id, name: editTaskName.trim() });
                                                if (e.key === 'Escape') setEditingTask(null);
                                              }}
                                              onBlur={() => { if (editTaskName.trim() && editTaskName.trim() !== task.name) updateTaskName.mutate({ id: task.id, name: editTaskName.trim() }); else setEditingTask(null); }}
                                              className="px-2 py-1 text-sm border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                              autoFocus
                                            />
                                          ) : (
                                            <span
                                              className="text-sm text-gray-800 cursor-pointer hover:text-blue-600 hover:underline"
                                              onClick={() => { setEditingTask(task.id); setEditTaskName(task.name); }}
                                              title={t('cleaning.clickToEdit')}
                                            >
                                              {task.name}
                                            </span>
                                          )}
                                          <button
                                            onClick={() => updateTaskFreq.mutate({ id: task.id, frequency: task.frequency === 'DAILY' ? 'WEEKLY' : 'DAILY' })}
                                            className={`text-xs px-2 py-1 rounded-full font-medium ${
                                              task.frequency === 'DAILY'
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-purple-100 text-purple-700'
                                            }`}
                                          >
                                            {task.frequency === 'DAILY' ? t('cleaning.daily') : t('cleaning.weekly')}
                                          </button>
                                        </div>
                                        <button
                                          onClick={() => setDeleteTarget({ type: 'task', id: task.id, name: task.name })}
                                          className="text-red-400 hover:text-red-600 text-xs"
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    ))}

                                    {/* Add task form */}
                                    {addingTaskForEquipment === eq.id ? (
                                      <div className="flex flex-col sm:flex-row gap-2 mt-2">
                                        <input
                                          type="text"
                                          value={newTaskName}
                                          onChange={(e) => setNewTaskName(e.target.value)}
                                          onKeyDown={(e) => e.key === 'Enter' && newTaskName.trim() && createTask.mutate({ equipmentId: eq.id, name: newTaskName.trim(), frequency: newTaskFrequency })}
                                          placeholder={t('cleaning.taskNamePlaceholder')}
                                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                          autoFocus
                                        />
                                        <select
                                          value={newTaskFrequency}
                                          onChange={(e) => setNewTaskFrequency(e.target.value as 'DAILY' | 'WEEKLY')}
                                          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                        >
                                          <option value="DAILY">{t('cleaning.daily')}</option>
                                          <option value="WEEKLY">{t('cleaning.weekly')}</option>
                                        </select>
                                        <button
                                          onClick={() => newTaskName.trim() && createTask.mutate({ equipmentId: eq.id, name: newTaskName.trim(), frequency: newTaskFrequency })}
                                          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                                        >
                                          {t('common.add')}
                                        </button>
                                        <button
                                          onClick={() => { setAddingTaskForEquipment(null); setNewTaskName(''); }}
                                          className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                                        >
                                          {t('common.cancel')}
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        onClick={() => { setAddingTaskForEquipment(eq.id); setNewTaskName(''); setNewTaskFrequency('DAILY'); }}
                                        className="text-sm text-blue-600 hover:text-blue-800 font-medium mt-1"
                                      >
                                        + {t('cleaning.addTask')}
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}

                            {/* Add equipment form */}
                            {addingEquipmentForSubSector === subSector.id ? (
                              <div className="flex flex-col sm:flex-row gap-2">
                                <input
                                  type="text"
                                  value={newEquipmentName}
                                  onChange={(e) => setNewEquipmentName(e.target.value)}
                                  onKeyDown={(e) => e.key === 'Enter' && newEquipmentName.trim() && createEquipment.mutate({ subSectorId: subSector.id, name: newEquipmentName.trim() })}
                                  placeholder={t('cleaning.equipmentNamePlaceholder')}
                                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                  autoFocus
                                />
                                <button
                                  onClick={() => newEquipmentName.trim() && createEquipment.mutate({ subSectorId: subSector.id, name: newEquipmentName.trim() })}
                                  className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                                >
                                  {t('common.add')}
                                </button>
                                <button
                                  onClick={() => { setAddingEquipmentForSubSector(null); setNewEquipmentName(''); }}
                                  className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                                >
                                  {t('common.cancel')}
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => { setAddingEquipmentForSubSector(subSector.id); setNewEquipmentName(''); }}
                                className="text-sm text-green-600 hover:text-green-800 font-semibold"
                              >
                                + {t('cleaning.addEquipment')}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Add sub-sector form */}
                    {addingSubSectorForSector === sector.id ? (
                      <div className="flex flex-col gap-2 bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <div className="flex flex-col sm:flex-row gap-2">
                          <input
                            type="text"
                            value={newSubSectorName}
                            onChange={(e) => setNewSubSectorName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && newSubSectorName.trim() && createSubSector.mutate({ sectorId: sector.id, name: newSubSectorName.trim(), icon: newSubSectorIcon })}
                            placeholder={t('cleaning.subSectorNamePlaceholder')}
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none"
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={() => setShowSubSectorIconPicker((v) => !v)}
                            className="w-12 h-10 text-xl flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                            title={t('cleaning.chooseIcon')}
                          >
                            {newSubSectorIcon}
                          </button>
                          <button
                            onClick={() => newSubSectorName.trim() && createSubSector.mutate({ sectorId: sector.id, name: newSubSectorName.trim(), icon: newSubSectorIcon })}
                            disabled={!newSubSectorName.trim() || createSubSector.isPending}
                            className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition"
                          >
                            {t('common.add')}
                          </button>
                          <button
                            onClick={() => { setAddingSubSectorForSector(null); setNewSubSectorName(''); setNewSubSectorIcon('📍'); setShowSubSectorIconPicker(false); }}
                            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                        {showSubSectorIconPicker && (
                          <IconPicker
                            icons={SUBSECTOR_ICONS}
                            value={newSubSectorIcon}
                            onChange={(icon) => { setNewSubSectorIcon(icon); setShowSubSectorIconPicker(false); }}
                          />
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => { setAddingSubSectorForSector(sector.id); setNewSubSectorName(''); setNewSubSectorIcon('📍'); }}
                        className="text-sm text-purple-600 hover:text-purple-800 font-semibold"
                      >
                        + {t('cleaning.addSubSector')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}

            {sectors.length === 0 && !isLoading && (
              <div className="bg-white rounded-xl shadow p-8 text-center">
                <p className="text-gray-500 text-lg">{t('cleaning.noSectors')}</p>
                <p className="text-gray-400 text-sm mt-2">{t('cleaning.noSectorsHint')}</p>
              </div>
            )}
          </div>
        )}

        {/* Delete confirmation dialog */}
        <ConfirmDialog
          open={!!deleteTarget}
          title={
            deleteTarget?.type === 'sector' ? t('cleaning.confirmDeleteSector') :
            deleteTarget?.type === 'subsector' ? t('cleaning.confirmDeleteSubSector') :
            deleteTarget?.type === 'equipment' ? t('cleaning.confirmDeleteEquipment') :
            t('cleaning.confirmDeleteTask')
          }
          message={t('cleaning.confirmDeleteMessage', { name: deleteTarget?.name })}
          onConfirm={() => {
            if (deleteTarget?.type === 'sector') deleteSector.mutate(deleteTarget.id);
            else if (deleteTarget?.type === 'subsector') deleteSubSector.mutate(deleteTarget.id);
            else if (deleteTarget?.type === 'equipment') deleteEquipment.mutate(deleteTarget.id);
            else if (deleteTarget?.type === 'task') deleteTask.mutate(deleteTarget.id);
            setDeleteTarget(null);
          }}
          onCancel={() => setDeleteTarget(null)}
        />
      </div>
    </div>
  );
}
